import { FC, FormEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Autocomplete, Chip, Grid, Stack, styled } from '@mui/material';

import { Button, Icon, TextField } from '@components/base';
import { CasingInputHint } from '@components/forms';
import { camelCasePatternWithUnderscore } from '@utils/stringUtils';

import { KeyValue } from './types';
import usePredefinedParams from './usePredefinedParams';

type NewKeyValuePairProps = {
  handleSetPair: (keyValuePair: KeyValue) => void;
  reservedKeys?: string[];
  usedKeys: string[];
};

const newKeyValuePair: KeyValue = { key: '', value: '' };

const StyledForm = styled('form')`
  width: 100%;
`;

const NewKeyValuePair: FC<NewKeyValuePairProps> = ({ handleSetPair, usedKeys, reservedKeys }) => {
  const { t } = useTranslation();

  const [isAdding, setIsAdding] = useState(false);
  const [newPair, setNewPair] = useState(newKeyValuePair);
  const [reservedKeyError, setReservedKeyError] = useState(false);

  const reset = () => {
    setIsAdding(false);
    setNewPair(newKeyValuePair);
    setReservedKeyError(false);
  };

  const submit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    if (reservedKeys?.includes(newPair.key)) setReservedKeyError(true);
    else if (newPair.key && newPair.value) {
      handleSetPair(newPair);
      reset();
    }
  };

  const { getDefsForParam, getFilteredParams } = usePredefinedParams();

  return isAdding ? (
    <StyledForm onSubmit={submit}>
      <Stack direction='column' spacing={{ xs: 1, sm: 2 }} width={1}>
        <div>
          <Grid alignItems='center' container spacing={{ xs: 1, sm: 2 }}>
            <Grid size={{ xs: 12, sm: 'grow' }}>
              <Autocomplete
                aria-required
                autoFocus
                disableClearable
                freeSolo
                fullWidth
                getOptionLabel={option => (typeof option === 'string' ? option : option.value)}
                onChange={(_, value) =>
                  setNewPair(prev => ({
                    ...prev,
                    key: typeof value === 'string' ? value : value?.value || '',
                  }))
                }
                options={getFilteredParams(usedKeys)}
                renderInput={params => (
                  <TextField
                    {...params}
                    autoComplete='on'
                    label={t('key')}
                    name='custom-param-key'
                    onChange={({ currentTarget: { value } }) =>
                      setNewPair(prev => ({ ...prev, key: value }))
                    }
                    required
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: <CasingInputHint casing='camel' position='end' />,
                      },

                      htmlInput: { ...params.inputProps, pattern: camelCasePatternWithUnderscore },
                    }}
                  />
                )}
                value={newPair.key}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 'grow' }}>
              <TextField
                fullWidth
                label={t('value')}
                name={newPair.key || undefined}
                onChange={({ currentTarget: { value } }) =>
                  setNewPair(prev => ({ ...prev, value }))
                }
                required
                value={newPair.value}
                {...(getDefsForParam(newPair.key)?.isUrl
                  ? { type: 'url', placeholder: 'https://example.com' }
                  : {})}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 'auto' }}>
              <Stack direction='row' spacing={{ xs: 1, sm: 2 }}>
                <Button type='submit' variant='contained'>
                  {t('add')}
                </Button>

                <Button onClick={reset}>{t('cancel')}</Button>
              </Stack>
            </Grid>
          </Grid>
        </div>

        {reservedKeyError && <Alert severity='error'>{t('customParams.reserverKeyError')}</Alert>}
      </Stack>
    </StyledForm>
  ) : (
    <Chip
      avatar={<Icon fontSize='small' name='add' />}
      disabled={isAdding}
      key='add-key-value-pair'
      label={t('add')}
      onClick={() => setIsAdding(true)}
      size='small'
    />
  );
};

export default NewKeyValuePair;
